import { useEffect, useRef, useState, useCallback } from "react";
import { listarDispositivos } from "../API/dispositivos-crud/listarDispositivos";
import { adicionarDispositivo } from "../API/dispositivos-crud/adicionarDispositivo";
import { editarDispositivo } from "../API/dispositivos-crud/editarDispositivo";
import { deletarDispositivo } from "../API/dispositivos-crud/deletarDispositivo";

/**
 * Hook de CRUD + polling sem flicker:
 * - loadingInitial: apenas na primeira carga
 * - isFetching: indica polling em andamento (sem esconder a tabela)
 * - merge por id preservando referências quando nada mudou
 * - pausa polling quando a aba está oculta
 */
export function useDispositivos(pollingMs = 10000) {
  const [devices, setDevices] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const startedRef = useRef(false);
  const intervalRef = useRef(null);

  const normalizeResposta = (resposta) => {
    if (Array.isArray(resposta)) return resposta;
    if (resposta?.data) return Array.isArray(resposta.data) ? resposta.data : [resposta.data];
    if (resposta) return [resposta];
    return [];
  };

  // compara campos relevantes para decidir se o objeto mudou
  const hasChanged = (oldD, newD) => {
    if (!oldD) return true;
    return (
      oldD.name !== newD.name ||
      oldD.ip !== newD.ip ||
      oldD.description !== newD.description ||
      oldD.active !== newD.active ||
      oldD.is_unstable !== newD.is_unstable ||
      (oldD.instability?.is_unstable ?? undefined) !== (newD.instability?.is_unstable ?? undefined) ||
      (oldD.last_ping?.alive ?? undefined) !== (newD.last_ping?.alive ?? undefined) ||
      (oldD.last_ping?.response_time ?? undefined) !== (newD.last_ping?.response_time ?? undefined)
    );
  };

  // aplica merge por id preservando referência quando não há mudanças
  const mergeDevices = useCallback((prev, next) => {
    const prevById = new Map(prev.map(d => [d.id, d]));
    const ordered = [...next].sort((a, b) => String(a.id).localeCompare(String(b.id))); // ordem estável opcional
    return ordered.map(n => {
      const old = prevById.get(n.id);
      return hasChanged(old, n) ? n : old;
    });
  }, []);

  const refresh = useCallback(async () => {
    // cancela requisição anterior se ainda estiver viva
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    try {
      setError(null);
      // Só mostra carregamento inicial antes da primeira resposta
      if (!startedRef.current) setLoadingInitial(true);
      setIsFetching(true);

      const resposta = await listarDispositivos({ signal: ac.signal });
      const lista = normalizeResposta(resposta);

      setDevices(prev => mergeDevices(prev, lista));
    } catch (err) {
      if (err?.name !== "AbortError") {
        console.error("Erro ao buscar dispositivos", err);
        setError(err);
      }
    } finally {
      startedRef.current = true;
      setLoadingInitial(false);
      setIsFetching(false);
    }
  }, [mergeDevices]);

  // polling com pausa quando a aba está oculta
  useEffect(() => {
    let active = true;

    const tick = async () => {
      if (!active) return;
      // pausa quando a aba não está visível
      if (document.visibilityState === "visible") {
        await refresh();
      }
    };

    // primeira carga imediata
    tick();

    // intervalo
    intervalRef.current = setInterval(tick, pollingMs);

    // listener de visibilidade: quando voltar a focar, faz um refresh rápido
    const onVis = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      active = false;
      document.removeEventListener("visibilitychange", onVis);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [refresh, pollingMs]);

  /** CRUD com refresh; se quiser, dá pra otimizar de forma otimista também */
  const addDevice = useCallback(async (payload) => {
    await adicionarDispositivo(payload);
    await refresh();
  }, [refresh]);

  const updateDevice = useCallback(async (id, payload) => {
    await editarDispositivo(id, payload);
    await refresh();
  }, [refresh]);

  const removeDevice = useCallback(async (id) => {
    await deletarDispositivo(id);
    await refresh();
  }, [refresh]);

  return {
    devices,
    loadingInitial, // use isso na tela para o "Carregando..."
    isFetching,     // e isso para um badge "Atualizando…", sem esconder a tabela
    error,
    addDevice,
    updateDevice,
    removeDevice,
    refresh,        // expõe se quiser forçar um refresh manual
  };
}
